import { useState, useEffect, useRef } from "react";
import { RevisionStatus } from "shared/utils/enum";
import {
  GetLatestFeedbackService,
  GetRevisionFeedbackService,
  UpdateRevisionInspirationsService,
} from "shared/services/revision";
import { handleErrors } from "shared/utils/helper";
import { toastMessage } from "shared/components/common/toast";
import { Icons } from "assets";
import CustomPageHeading from "shared/components/common/customPageHeading";
import classNames from "classnames";
import styles from "./style.module.scss";
import moment from "moment";
import FeedbackRequestSkeleton from "shared/components/common/feedbackRequestSkeleton";
import NoContentCard from "shared/components/common/noContentCard";
import CustomBadge from "shared/components/common/customBadge";
import CustomButton from "shared/components/common/customButton";
import CustomDeleteButton from "shared/components/common/customDeleteButton";
import CustomTextEditor from "shared/components/common/customTextEditor";
import { ImageItem } from "shared/components/common/imageItemsList";
import AddInspirationModal from "shared/modal/addInspirationModal";

interface SubmittedRequestProps {
  projectId?: number;
  revisionId?: number;
  mobileFilterDropdown?: React.ReactNode;
  isArchived?: boolean;
}

const SubmittedRequest = ({
  projectId,
  revisionId,
  mobileFilterDropdown,
  isArchived = false,
}: SubmittedRequestProps) => {
  const [loading, setLoading] = useState(false);
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deletedInspirationIndices, setDeletedInspirationIndices] = useState<
    Set<number>
  >(new Set());
  const [addInspirationModalOpen, setAddInspirationModalOpen] = useState(false);
  const [newInspirationItems, setNewInspirationItems] = useState<ImageItem[]>(
    [],
  );
  const [isAddingInspirations, setIsAddingInspirations] = useState(false);
  const [editClientComment, setEditClientComment] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const lastFetchedKeyRef = useRef<string | undefined>(undefined);
  const isFetchingRef = useRef<boolean>(false);

  useEffect(() => {
    if (feedbackData) {
      setDeletedInspirationIndices(new Set());
      setAddInspirationModalOpen(false);
      setNewInspirationItems([]);
      setEditClientComment(feedbackData.client_comment || "");
    }
  }, [feedbackData]);

  useEffect(() => {
    const key = revisionId ? `r-${revisionId}` : projectId ? `p-${projectId}` : undefined;
    if (!key) {
      lastFetchedKeyRef.current = undefined;
      setFeedbackData(null);
      isFetchingRef.current = false;
      return;
    }

    if (key !== lastFetchedKeyRef.current && !isFetchingRef.current) {
      fetchFeedbackData(key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when revision/project changes
  }, [revisionId, projectId]);

  const fetchFeedbackData = async (key: string) => {
    if (key === lastFetchedKeyRef.current || isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      lastFetchedKeyRef.current = key;
      const response = revisionId
        ? await GetRevisionFeedbackService(revisionId)
        : projectId
          ? await GetLatestFeedbackService(projectId)
          : null;

      if (response && response.status === 200) {
        const data = response.data?.data || response.data;
        setFeedbackData(data);
      }
    } catch (error: any) {
      setFeedbackData(null);
      lastFetchedKeyRef.current = undefined;
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  if (!projectId) {
    return (
      <div className={classNames(styles.sectionContainer, "pt-3")}>
        <div className="flex items-center justify-center py-8">
          <NoContentCard
            Icon={Icons.TabFeedback}
            label1="No project selected"
            label2="Please select a project to view feedback."
          />
        </div>
      </div>
    );
  }

  if (loading) {
    return <FeedbackRequestSkeleton />;
  }

  if (!feedbackData) {
    return (
      <div className={classNames(styles.sectionContainer, "pt-3")}>
        <div className="flex items-center justify-center h-[600px]">
          <NoContentCard
            Icon={Icons.NoFeedbackIcon}
            label1="No revision requests yet"
            label2=""
          />
        </div>
      </div>
    );
  }

  const allInspirations = feedbackData.inspirations || [];
  const inspirations = isEditMode
    ? allInspirations.filter(
        (_: any, index: number) => !deletedInspirationIndices.has(index),
      )
    : allInspirations;

  const handleDeleteInspiration = (originalIndex: number) => {
    setDeletedInspirationIndices((prev) => new Set(prev).add(originalIndex));
  };

  const handleSave = async () => {
    const revisionId =
      feedbackData?.revision_id ??
      feedbackData?.revisionId ??
      feedbackData?.revision?.revision_id ??
      feedbackData?.revision?.id;

    if (!revisionId) {
      toastMessage("error", "Revision ID is missing");
      return;
    }

    try {
      setIsSaving(true);

      const formData = new FormData();
      formData.append("client_comment", (editClientComment || "").trim());

      const deleteIds = allInspirations
        .filter((_: any, index: number) => deletedInspirationIndices.has(index))
        .map((item: any) => item.revision_inspiration_id ?? item.id)
        .filter((id: any): id is number => typeof id === "number");

      deleteIds.forEach((id: number) =>
        formData.append("delete_inspiration_ids[]", id.toString()),
      );

      const itemsWithFiles = newInspirationItems.filter(
        (item: any) => item.hasImage && item.file instanceof File,
      );

      itemsWithFiles.forEach((item: any, index: number) => {
        formData.append(
          `inspirations[${index}][label]`,
          item.imageLabel || "Untitled",
        );
        formData.append(
          `inspirations[${index}][additional_notes]`,
          item.notes || "",
        );
        if (item.file instanceof File) {
          formData.append(
            `inspirations[${index}][inspiration_images]`,
            item.file,
          );
        }
      });

      const response = await UpdateRevisionInspirationsService(
        revisionId,
        formData,
      );

      if (response.status === 200 || response.status === 201) {
        toastMessage("success", "Changes saved successfully!");
        setIsEditMode(false);
        lastFetchedKeyRef.current = undefined;
        const key = revisionId
          ? `r-${revisionId}`
          : projectId
            ? `p-${projectId}`
            : undefined;
        if (key) fetchFeedbackData(key);
      } else {
        handleErrors(response);
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddInspirationsFromModal = async (items: ImageItem[]) => {
    const revisionId =
      feedbackData?.revision_id ??
      feedbackData?.revisionId ??
      feedbackData?.revision?.revision_id ??
      feedbackData?.revision?.id;

    if (!revisionId) {
      toastMessage("error", "Revision ID is missing");
      return;
    }

    const itemsWithFiles = items.filter(
      (item: any) => item.hasImage && item.file instanceof File,
    );

    if (itemsWithFiles.length === 0) {
      return;
    }

    try {
      setIsAddingInspirations(true);

      const formData = new FormData();
      formData.append("client_comment", (editClientComment || "").trim());

      itemsWithFiles.forEach((item: any, index: number) => {
        formData.append(
          `inspirations[${index}][label]`,
          item.imageLabel || "Untitled",
        );
        formData.append(
          `inspirations[${index}][additional_notes]`,
          item.notes || "",
        );
        if (item.file instanceof File) {
          formData.append(
            `inspirations[${index}][inspiration_images]`,
            item.file,
          );
        }
      });

      const response = await UpdateRevisionInspirationsService(
        revisionId,
        formData,
      );

      if (response.status === 200 || response.status === 201) {
        toastMessage("success", "Inspirations added successfully!");
        setAddInspirationModalOpen(false);
        setNewInspirationItems([]);
        lastFetchedKeyRef.current = undefined;
        const key = revisionId
          ? `r-${revisionId}`
          : projectId
            ? `p-${projectId}`
            : undefined;
        if (key) fetchFeedbackData(key);
      } else {
        handleErrors(response);
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      setIsAddingInspirations(false);
    }
  };

  const feedbackUser = feedbackData.feedback_user;
  const feedbackDate = feedbackData.client_feedback_date
    ? moment(feedbackData.client_feedback_date).format("MM/DD/YYYY HH:mm")
    : "";

  const revisionStatus = (feedbackData.revision_status || "")
    .toLowerCase()
    .replace(/[\s-]/g, "_");

  const isPendingForClient =
    revisionStatus === RevisionStatus.WAITING_FOR_CLIENT_RESPONSE;

  const hasNoData =
    !feedbackData.revision_title &&
    !feedbackUser &&
    !feedbackData.client_comment &&
    (!allInspirations || allInspirations.length === 0);

  if (hasNoData || isPendingForClient) {
    return (
      <div className={classNames(styles.sectionContainer, "pt-3")}>
        <div className="flex items-center justify-center h-[600px]">
          <NoContentCard
            Icon={Icons.NoFeedbackIcon}
            label1="No revision requests yet"
            label2=""
          />
        </div>
      </div>
    );
  }

  const isEditAllowed = !isArchived && revisionStatus === RevisionStatus.IN_REVIEW;

  const getDescription = () => {
    if (!feedbackUser) {
      return undefined;
    }

    const userName =
      feedbackUser.display_name ||
      `${feedbackUser.first_name || ""} ${feedbackUser.last_name || ""}`.trim() ||
      "Unknown User";

    return `Uploaded by ${userName}${feedbackDate ? ` on ${feedbackDate}` : ""}`;
  };

  return (
    <div className={classNames(styles.sectionContainer, "")}>
      <div className="flex flex-col gap-5 md:mt-0 mt-4 pt-3">
        <div className="flex items-center gap-2 justify-between">
          <CustomPageHeading
            title={`${feedbackData.revision_title || "Latest Revision"} Feedback`}
            size="medium"
            description={getDescription()}
            isHandleIcon={true}
          />
          <CustomBadge
            title={feedbackData.revision_status}
            statusType={feedbackData.revision_status}
          />
        </div>
        {isEditMode ? (
          <>
            <CustomPageHeading title="Comments" size="medium" />
            <CustomTextEditor
              value={editClientComment}
              onChange={(value) => setEditClientComment(value)}
              placeholder="Type your message here..."
              customContainerStyle={styles.commentsEditor}
              customToolbarStyle={styles.commentsToolbar}
            />
          </>
        ) : (
          feedbackData.client_comment && (
            <CustomPageHeading
              title="Comments"
              size="medium"
              description={feedbackData.client_comment}
              renderHtml={true}
            />
          )
        )}
        {(inspirations.length > 0 || isEditMode) && (
          <div className={styles.inspirationFilesContainer}>
            <CustomPageHeading title="Inspiration Files" size="medium" />

            <div
              className={classNames(
                styles.inspirationFilesList,
                "grid md:grid-cols-2 grid-cols-1 lg:gap-6 md:gap-5 gap-4 mt-3 mb-2",
              )}
            >
              {isEditMode && (
                <div className={styles.addMoreGridItem}>
                  <button
                    type="button"
                    className={styles.addMoreTrigger}
                    onClick={() => setAddInspirationModalOpen(true)}
                  >
                    <Icons.CameraPlus className={styles.addMoreIcon} />
                    <span>
                      {newInspirationItems.length > 0
                        ? `${newInspirationItems.length} image(s) added — Add More`
                        : "Add More Image"}
                    </span>
                  </button>
                </div>
              )}
              {inspirations.map((file: any, index: number) => {
                const originalIndex = allInspirations.indexOf(file);
                return (
                  <div
                    key={file.revision_inspiration_id ?? file.id ?? index}
                    className={styles.inspirationFileItem}
                  >
                    {isEditMode && (
                      <CustomDeleteButton
                        onClick={() => handleDeleteInspiration(originalIndex)}
                        className={styles.deleteButton}
                        ariaLabel="Delete this inspiration"
                      />
                    )}
                    <div className="flex flex-col">
                      <div className="flex gap-4">
                        <span className={styles.imgContainer}>
                          {file.type && file.type !== "image" ? (
                            <a
                              href={file.path}
                              target="_blank"
                              rel="noreferrer"
                              className={styles.fileTypeThumb}
                              title="Click to view file"
                            >
                              <Icons.FilePdfIcon />
                              <span className={styles.fileTypeThumbLabel}>
                                {file.name || "Click to view"}
                              </span>
                            </a>
                          ) : (
                            <img src={file.path} alt={file.label} />
                          )}
                        </span>
                        <CustomPageHeading
                          title="Label:"
                          description={file.label}
                          size="xsmall"
                        />
                      </div>

                      <div
                        className={classNames(
                          styles.additionalNotesContainer,
                          "xs:ml-4",
                        )}
                      >
                        <CustomPageHeading
                          title="Additional Notes: "
                          description={
                            file.additional_notes || "No additional notes"
                          }
                          size="xsmall"
                          renderHtml={true}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!isArchived && (
        <div className="flex flex-col gap-3">
          <CustomButton
            Icon={Icons.EditIcon}
            IconDirection="left"
            title={
              isEditMode
                ? isSaving
                  ? "Saving..."
                  : "Save Changes"
                : "Edit Request"
            }
            onClick={() => (isEditMode ? handleSave() : setIsEditMode(true))}
            containerStyle="primary-btn"
            disabled={!isEditAllowed || isSaving}
          />
          {!isEditAllowed && (
            <div className={styles.editDisabledBar}>
              <Icons.Info className={styles.editDisabledIcon} />
              <span className={styles.editDisabledText}>
                Unable to make more edits as the designer is currently working
                on the changes
              </span>
            </div>
          )}
        </div>
        )}
      </div>

      <AddInspirationModal
        isOpen={addInspirationModalOpen}
        onClose={() => setAddInspirationModalOpen(false)}
        existingItems={newInspirationItems}
        onSubmit={handleAddInspirationsFromModal}
        isSubmitting={isAddingInspirations}
      />
    </div>
  );
};

export default SubmittedRequest;
